// Package imports:
import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
// Style imports:
import './HomeAlertSingleReviewModal.scss';
// Component imports:
import Button from 'react-bootstrap/Button';
import DuplicateMassReviewModal from './DuplicateMassReviewModal';
// Action imports:
import { requestAlertReviewHistory, addAlertReviewHistoryEntry } from '../../redux-store/actions/alertActions';
// Type imports:
import { RootState } from '../../redux-store/reducers';
import { AlertStatus, AlertTickData, IAlertReviewAttachment } from '../../types/api/AlertTypes';
import NewReview from './singlereviewparts/NewReview';
import AlertInfoV2 from './singlereviewparts/AlertInfoV2';
import AlertLastReview from './singlereviewparts/AlertLastReview';
import AlertHistoryV2 from './singlereviewparts/AlertHistoryV2';
import { formatTime, uploadAllAttachments } from '../../services/utils';

interface IOwnProps {
    alert: AlertTickData;
    allAlerts: AlertTickData[];
    onHide(): void;
};
type ReduxProps = ConnectedProps<typeof connector>
type Props = ReduxProps & IOwnProps;

type Tabs = 'New Review' | "Alert Information" | "Last Review" | 'History';
const TABS: Tabs[] = ['New Review', 'Alert Information', 'Last Review', 'History'];

/**
 * Component to display a single alert for review. **PopUp Review Alert Model**
 * - Subcomponent of HomeAlerts.tsx
 * - Subcomponent of MyAlerts.tsx
 */
const AlertReviewModal: React.FC<Props & { allAlerts: AlertTickData[] }> = ({
    alert,
    allAlerts,
    onHide,
    accessTokenV2,
    alertReviewHistoryByAlertDataId,
    requestAlertReviewHistory,
    addAlertReviewHistoryEntry
}) => {
    // Initialize states.
    const [ selectedTab, setSelectedTab ] = useState<Tabs>('New Review');
    const [ comment, setComment ] = useState('');
    const [ newStatus, setNewStatus ] = useState<AlertStatus>(alert.status || 'open');
    const [ attachments, setAttachments] = useState<File[]>([]);
    const [ canSave, setCanSave] = useState(true);
    const [ minimized, setMinimized ] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [duplicateAlerts, setDuplicateAlerts] = useState<AlertTickData[]>([]);
    const [pendingSave, setPendingSave] = useState<{
        comment: string;
        newStatus: AlertStatus;
        attachments: File[];
    } | null>(null);

    // Fetch data.
    useEffect(() => {
        const alertDataId = alert.ad_id;
        if (alertDataId && (alertReviewHistoryByAlertDataId[alertDataId] === undefined)) {
            requestAlertReviewHistory(accessTokenV2 || "", alertDataId);
        }
    }, [alert, accessTokenV2, alertReviewHistoryByAlertDataId, requestAlertReviewHistory]);

    const handleHeaderClick = () => {
        if (minimized) {
            setMinimized(false);
        } else {
            setMinimized(true);
        }
    };

    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    
    // Helper to check for duplicates
    const findDuplicateAlerts = () => {
        if (alert.id !== 20 || !alert.symbol || !alert.newstitle || !alert.timestamp) return [];
        if (!allAlerts || !Array.isArray(allAlerts)) return [];
        const alertTimestamp = new Date(alert.timestamp).getTime();
        const fourteenDaysMs = 14 * 24 * 60 * 60 * 1000;
        const now = Date.now();
        // Find alerts with timestamp >= (alertTimestamp - 14 days) and <= now
        return allAlerts.filter(a =>
            a.id === 20 &&
            a.symbol === alert.symbol &&
            a.newstitle === alert.newstitle &&
            a.timestamp &&
            new Date(a.timestamp).getTime() >= (alertTimestamp - fourteenDaysMs) &&
            new Date(a.timestamp).getTime() <= now
        );
    };

    const handleSave = async () => {
        setUploadError(null);
        // Check for duplicates before saving
        if (alert.id === 20 && alert.symbol && alert.newstitle && alert.timestamp) {
            const duplicates = findDuplicateAlerts();
            // Only show modal if more than 1 (otherwise it's just this alert)
            if (duplicates.length > 1) {
                setDuplicateAlerts(duplicates);
                setPendingSave({ comment, newStatus, attachments });
                setShowDuplicateModal(true);
                return;
            }
        }
        // ...existing code for saving single review...
        try {
            setUploading(true);
            const reviewResponse = await addAlertReviewHistoryEntry(
                accessTokenV2 || "",
                [alert.ad_id ?? 0],
                comment,
                newStatus,
                []
            ) as any;

            const newReview = Array.isArray(reviewResponse) ? reviewResponse[reviewResponse.length -1] : undefined;
            const reviewId = newReview?.review_id;
            const groupId = newReview?.groupid;

            let uploadedAttachments: IAlertReviewAttachment[] = [];
            if (attachments.length > 0 && reviewId && groupId) {
                uploadedAttachments = await uploadAllAttachments(attachments, reviewId, groupId, accessTokenV2);
                if (uploadedAttachments.length !== attachments.length) {
                    throw new Error('Not all files were uploaded successfully.');
                }
                await requestAlertReviewHistory(accessTokenV2 || "", alert.ad_id ?? 0);
            } else if (attachments.length > 0) {
                console.warn('[AlertReviewModal] Skipping file upload: missing reviewId or groupId');
            }
            // Restore closing the modal after save
            onHide();
        } catch (err: any) {
            setUploadError(err.message || 'File upload failed');
        } finally {
            setUploading(false);
        }
    };

    // Handler for mass review after user accepts
    const handleMassReview = async () => {
        setShowDuplicateModal(false);
        if (!pendingSave) return;
        setUploadError(null);
        try {
            setUploading(true);
            await addAlertReviewHistoryEntry(
                accessTokenV2 || "",
                duplicateAlerts.map(a => a.ad_id ?? 0),
                pendingSave.comment,
                pendingSave.newStatus,
                []
            );
            onHide();
        } catch (err: any) {
            setUploadError(err.message || 'Mass review failed');
        } finally {
            setUploading(false);
            setPendingSave(null);
        }
    };

    // Handler for declining mass review
    const handleDeclineMassReview = async () => {
        setShowDuplicateModal(false);
        setPendingSave(null);
        // Save only the current alert, not all duplicates
        setUploadError(null);
        try {
            setUploading(true);
            const reviewResponse = await addAlertReviewHistoryEntry(
                accessTokenV2 || "",
                [alert.ad_id ?? 0],
                comment,
                newStatus,
                []
            ) as any;
            const newReview = Array.isArray(reviewResponse) ? reviewResponse[reviewResponse.length -1] : undefined;
            const reviewId = newReview?.review_id;
            const groupId = newReview?.groupid;
            let uploadedAttachments: IAlertReviewAttachment[] = [];
            if (attachments.length > 0 && reviewId && groupId) {
                uploadedAttachments = await uploadAllAttachments(attachments, reviewId, groupId, accessTokenV2);
                if (uploadedAttachments.length !== attachments.length) {
                    throw new Error('Not all files were uploaded successfully.');
                }
                await requestAlertReviewHistory(accessTokenV2 || "", alert.ad_id ?? 0);
            } else if (attachments.length > 0) {
                console.warn('[AlertReviewModal] Skipping file upload: missing reviewId or groupId');
            }
            onHide();
        } catch (err: any) {
            setUploadError(err.message || 'File upload failed');
        } finally {
            setUploading(false);
        }
    };

    // Handler for status and comment changes in the duplicate modal
    const handleDuplicateStatusChange = (status: AlertStatus) => {
        setNewStatus(status);
        if (pendingSave) setPendingSave({ ...pendingSave, newStatus: status });
    };
    const handleDuplicateCommentChange = (comment: string) => {
        setComment(comment);
        if (pendingSave) setPendingSave({ ...pendingSave, comment });
    };

    // Ensure Save is only enabled if file validation passes (or no files)
    // Save should be disabled if: uploading, or (user tried to upload and there are no valid files), or (there are files and not all are valid)
    const isSaveDisabled = uploading || !canSave;

    const handleFileValidationChange = (isValid: boolean) => {
        setCanSave(isValid);
    };

    // Find the latest review for this alert from Redux (alertReviewHistoryByAlertDataId)
    const alertDataId = alert?.ad_id ?? 0;
    const reviewHistoryRaw = alertDataId && alertReviewHistoryByAlertDataId[alertDataId];
    const reviewHistory = Array.isArray(reviewHistoryRaw) ? reviewHistoryRaw : [];
    let latestReview: AlertTickData = alert;
    if (reviewHistory.length > 0) {
        const reviewFromRedux = reviewHistory[reviewHistory.length - 1];
        // Map groupid from Redux review to adu_groupid for AlertLastReview
        latestReview = {
            ...alert,
            ...reviewFromRedux,
            review_id: reviewFromRedux.review_id || alert.review_id,
            adu_groupid: (reviewFromRedux.groupid || alert.adu_groupid),
        };
    }

    return (
        <>
            <div className={'AlertReviewModal-container' + (minimized ? ' minimized' : '')}>
                <div className='AlertReviewModal-header' onClick={handleHeaderClick}>
                    <div className='AlertReviewModal-header-text'>
                        <div>{alert.description}</div>
                        <div>•</div>
                        <div>{formatTime(alert.timestamp)}</div>
                    </div>
                    <div className='AlertReviewModal-header-buttons'>
                        {minimized ? null : <div className='button' onClick={() => setMinimized(!minimized)}>__</div>}
                        <div className='button' onClick={onHide}>X</div>
                    </div>
                </div>
                {
                    minimized ? null : (
                        <>
                            <div className='AlertReviewModal-body'>
                            <div className='PanelTabSelector'>
                                <div className='BackgroundBar' />
                                <div className='PanelTabs'>
                                    {
                                        TABS
                                        // Turn array into JSX.
                                        .map(tabTitle => (
                                            <div
                                                key={tabTitle}
                                                onClick={() => setSelectedTab(tabTitle)}
                                                className={'PanelTab' + ((selectedTab === tabTitle) ? ' SelectedTab' : '')}
                                            >
                                                {
                                                    (selectedTab === tabTitle)
                                                    ? <div className='SelectedTabBackground' />
                                                    : null
                                                }
                                                {tabTitle}
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                            {
                                (selectedTab === 'New Review') ?  (
                                    <NewReview 
                                        comment={comment} 
                                        setComment={setComment} 
                                        newStatus={newStatus} 
                                        setNewStatus={setNewStatus} 
                                        attachments={attachments}
                                        setAttachments={setAttachments}
                                        onFileValidationChange={handleFileValidationChange}
                                    />
                                ) : (selectedTab === 'Alert Information') ? (
                                    <AlertInfoV2 
                                        alert={alert} 
                                    />
                                ) : (selectedTab === 'Last Review') ? (
                                    <AlertLastReview 
                                        alert={latestReview}
                                        accessTokenV2={accessTokenV2}
                                    />
                                ) : (
                                    <AlertHistoryV2 
                                        alert={alert} 
                                        alertReviewHistoryByAlertDataId={alertReviewHistoryByAlertDataId} 
                                        accessTokenV2={accessTokenV2}
                                    />
                                )
                            }
                        </div>
                        <div className='AlertReviewModal-footer'>
                            {
                                (selectedTab === 'New Review') && (
                                    <div className="footer-flex-row">
                                        {uploadError && <div className="error upload-error-left">{uploadError}</div>}
                                        <Button
                                            className='SaveButton'
                                            variant='dark'
                                            onClick={handleSave}
                                            disabled={isSaveDisabled}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                )
                            }
                        </div>
                    </>
                    )
                }
            </div>
            <DuplicateMassReviewModal
                show={showDuplicateModal}
                duplicates={duplicateAlerts}
                defaultStatus={newStatus}
                defaultComment={comment}
                onStatusChange={handleDuplicateStatusChange}
                onCommentChange={handleDuplicateCommentChange}
                onAccept={handleMassReview}
                onDecline={handleDeclineMassReview}
            />
        </>
    );
}

function mapStateToProps({ alertReducer, loginReducer }: RootState) {
    const { alertReviewHistoryByAlertDataId } = alertReducer;
    const { accessTokenV2 } = loginReducer;
    return {
        accessTokenV2,
        alertReviewHistoryByAlertDataId
    };
}

const connector = connect(mapStateToProps, { requestAlertReviewHistory, addAlertReviewHistoryEntry });
export default connector(AlertReviewModal);

// NOTE: To ensure the latest review/attachments show up after closing and reopening the modal,
// the parent component must re-fetch the alert data after a review is added.